/**
 * Workflow-detail tab components — Session 62f redesign.
 *
 * The old single-scroll workflow detail was overloaded (config + funnel
 * + tiles + A/B + enrollments + conversion trigger all on one page).
 * We split into four tabs so each surface owns one job:
 *   • WorkflowTab     — builder (the original "Steps" view, untouched)
 *   • AnalyticsTab    — engagement tiles + funnel chart + A/B table +
 *                       rolling 7-day bounce-rate trend
 *   • ClicksTab       — per-URL heat-map first-class + filterable
 *                       contact table + CSV export
 *   • EnrollmentsTab  — 3-status bucket view: contacted / in-queue /
 *                       scheduled, with filters, search and queue
 *                       position for every pending contact
 *
 * This file deliberately avoids restating the builder — WorkflowsPage
 * keeps the existing steps UI and mounts the correct tab under it.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  ReferenceArea, ResponsiveContainer, Tooltip as ReTooltip, Cell, LabelList,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { safeSrcDoc } from "@/lib/safeHtml";
import RichTextEditor from "./RichTextEditor";
import { formatLocal as formatLocalTz, formatLocalDate as formatLocalDateTz, asUtc } from "@/lib/timezone";

// ── Small shared helpers ────────────────────────────────────────────────
// Both helpers route through lib/timezone so timestamps render in the
// signed-in user's profile timezone (Mike → Pacific) regardless of the
// browser's clock. They also call asUtc() under the hood so naive ISO
// strings emitted by SQL Server DATETIME2 columns are interpreted as
// UTC instead of being mis-shifted by the viewer's local offset.
function formatLocal(iso) {
  if (!iso) return "";
  return formatLocalTz(iso, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", withZoneName: true,
  });
}
function formatLocalDate(iso) {
  if (!iso) return "";
  return formatLocalDateTz(iso);
}

// ═══════════════════════════════════════════════════════════════════════
// Tab nav bar
// ═══════════════════════════════════════════════════════════════════════
// Tab keys, also accepted as `?tab=` on the workflow URL.
export const WORKFLOW_TABS = ["workflow", "analytics", "clicks", "enrollments", "followup", "manual-send"];

export function WorkflowTabNav({ tab, onTab, counts = {} }) {
  // counts: { enrollments, clicks, opens, bounces } — small badges on
  // the tab labels so operators see activity even before entering a tab.
  const items = [
    { key: "workflow", label: "Workflow" },
    { key: "analytics", label: "Analytics" },
    { key: "clicks", label: "Clicks", badge: counts.clicks },
    { key: "enrollments", label: "Enrollments", badge: counts.enrollments },
    { key: "followup", label: "Follow-Up" },
    { key: "manual-send", label: "Manual Send" },
  ];
  return (
    <div className="border-b border-[#E5E5E5] mb-4 flex items-center gap-6" data-testid="wf-tab-nav">
      {items.map(it => (
        <button
          key={it.key}
          type="button"
          onClick={() => onTab(it.key)}
          className={`relative h-10 px-1 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
            tab === it.key
              ? "text-[#0055FF] border-b-2 border-[#0055FF]"
              : "text-[#525252] hover:text-[#0A0A0A] border-b-2 border-transparent"
          }`}
          data-testid={`wf-tab-${it.key}`}
        >
          <span>{it.label}</span>
          {it.badge != null && it.badge > 0 && (
            <span className="ml-2 inline-block min-w-[20px] px-1.5 py-0.5 text-[10px] bg-[#F5F5F5] text-[#525252] rounded-sm tabular-nums">
              {it.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Winning-variant detector (Analytics tab)
// ═══════════════════════════════════════════════════════════════════════
export function VariantWinnerBanner({ workflowId, onPauseVariant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("opens_real");
  const [pausing, setPausing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/marketing/workflows/${workflowId}/variant-significance`,
            { params: { metric, min_sends_per_variant: 50, confidence: 0.95 } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId, metric]);

  if (loading || !data || data.steps.length === 0) return null;

  const handlePause = async (stepIdx, variantId) => {
    if (!window.confirm(
      `Pause variant ${variantId} on step ${stepIdx}?\n\n` +
      `New enrollments at this step will route to the leader instead. ` +
      `Existing in-flight sends are unaffected.`
    )) return;
    setPausing(`${stepIdx}-${variantId}`);
    try {
      await onPauseVariant?.(stepIdx, variantId);
      // Reload after pause
      const r = await api.get(`/marketing/workflows/${workflowId}/variant-significance`,
                               { params: { metric, min_sends_per_variant: 50, confidence: 0.95 } });
      setData(r.data);
      toast.success(`Variant ${variantId} paused on step ${stepIdx}.`);
    } catch (e) {
      toast.error(`Pause failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPausing(null);
    }
  };

  const metricLabel = {
    opens_real: "real opens (humans only)",
    opens: "raw opens",
    clicks: "clicks",
    replies: "replies",
  }[metric];

  return (
    <div className="bg-white border border-[#E5E5E5] mt-3" data-testid="wf-variant-significance">
      <div className="px-4 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
          Winning-variant detector · 95% confidence
        </h3>
        <select value={metric} onChange={e => setMetric(e.target.value)}
                className="border border-[#E5E5E5] h-6 px-2 text-[10px]"
                data-testid="variant-significance-metric">
          <option value="opens_real">Real opens (humans only)</option>
          <option value="opens">Raw opens</option>
          <option value="clicks">Clicks</option>
          <option value="replies">Replies</option>
        </select>
      </div>
      <div className="divide-y divide-[#F5F5F5]">
        {data.steps.map(s => (
          <div key={s.step_index} className="p-4" data-testid={`variant-sig-step-${s.step_index}`}>
            <div className="flex items-baseline justify-between mb-2">
              <h4 className="text-xs font-bold text-[#0A0A0A]">
                Step {s.step_index} · {(s.step_subject_default || "").slice(0, 50)}
              </h4>
              {s.winner ? (
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#15803D] flex items-center gap-1">
                  🏆 Winner declared
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                  No significant winner yet
                </span>
              )}
            </div>

            <table className="w-full text-xs mb-2">
              <thead className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-1">Variant</th>
                  <th className="text-left py-1">Subject</th>
                  <th className="text-right py-1">Sends</th>
                  <th className="text-right py-1">{metricLabel}</th>
                  <th className="text-right py-1">Rate</th>
                </tr>
              </thead>
              <tbody>
                {s.variants.map(v => (
                  <tr key={v.variant_id} className={v.is_leader ? "bg-[#F0FDF4]" : ""}>
                    <td className="py-1 font-bold">{v.variant_id} {v.is_leader ? "🏆" : ""}</td>
                    <td className="py-1 text-[#525252] truncate max-w-[280px]">{v.subject || "—"}</td>
                    <td className="py-1 text-right tabular-nums">{v.sends}</td>
                    <td className="py-1 text-right tabular-nums">{v.successes}</td>
                    <td className="py-1 text-right tabular-nums font-bold">{(v.rate * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {s.winner ? (
              <div className="bg-[#F0FDF4] border border-[#16A34A] p-3 flex items-start justify-between gap-3"
                   data-testid={`variant-sig-winner-${s.step_index}`}>
                <div className="flex-1">
                  <div className="text-xs font-bold text-[#15803D] mb-1">
                    Variant <code>{s.winner.variant_id}</code> wins on {metricLabel}
                  </div>
                  <p className="text-[11px] text-[#0A0A0A]">
                    Lift over runner-up: <strong>+{(s.winner.lift_vs_runnerup * 100).toFixed(1)}pp</strong>.
                    {" "}Pair-wise p-value vs worst challenger:
                    {" "}<strong>{s.winner.p_value_vs_worst_challenger.toFixed(4)}</strong>.
                    {" "}Sample: {s.winner.sends} sends, {s.winner.successes} {metricLabel}.
                  </p>
                  <p className="text-[10px] text-[#525252] mt-1">
                    Recommend: pause <code>{s.winner.recommend_pause}</code> so future enrollments
                    route to the leader. New A/B traffic stays under control while the loser is dropped.
                  </p>
                </div>
                {onPauseVariant && (
                  <button
                    type="button"
                    onClick={() => handlePause(s.step_index, s.winner.recommend_pause)}
                    disabled={pausing === `${s.step_index}-${s.winner.recommend_pause}`}
                    className="bg-[#16A34A] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 hover:bg-[#15803D] disabled:opacity-40 whitespace-nowrap"
                    data-testid={`variant-sig-pause-${s.step_index}-${s.winner.recommend_pause}`}
                  >
                    Pause {s.winner.recommend_pause}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-[#A3A3A3]">
                {s.ineligible_reason ||
                  `Pair-wise p-values are not yet < ${s.alpha} — keep the variants running. ` +
                  s.pairwise.map(p => `${s.variants[0].variant_id} vs ${p.challenger}: p=${p.p_value.toFixed(3)}`).join("; ")
                }
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Adaptive cap status — shown on the Analytics tab so operators
// understand WHY their throughput is what it is.
// ═══════════════════════════════════════════════════════════════════════
export function AdaptiveCapStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/marketing/cap-status")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const bandColor = (b) => ({
    green: "#16A34A", amber: "#F59E0B", red: "#DC2626", starter: "#0055FF",
  }[b] || "#525252");
  const bandLabel = (b) => ({
    green: "Healthy", amber: "Caution", red: "Paused (red band)",
    starter: "Warm-up (starter cap)",
  }[b] || b);

  const bg = data.band === "red" ? "#FEF2F2"
           : data.band === "amber" ? "#FFFBEB"
           : data.band === "green" ? "#F0FDF4" : "#EFF6FF";
  const border = bandColor(data.band);

  return (
    <div className="border mt-3 p-4" style={{ background: bg, borderColor: border }}
         data-testid="adaptive-cap-status">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ color: border }}>
          Adaptive daily cap · {bandLabel(data.band)}
        </h3>
        <span className="text-[10px] text-[#525252] tabular-nums">
          7d rolling bounce rate:
          <strong className="ml-1" style={{ color: border }}>
            {(data.rate_7d * 100).toFixed(2)}%
          </strong>
          <span className="ml-1 text-[#A3A3A3]">({data.bounced_7d}/{data.sent_7d})</span>
        </span>
      </div>
      <div className="text-xs text-[#0A0A0A] mb-2">
        Today's effective cap: <strong className="text-2xl tabular-nums" style={{ color: border }}>
          {data.cap}
        </strong> sends/day
      </div>
      <p className="text-[11px] text-[#525252] leading-relaxed">{data.reason}</p>
      <div className="mt-3 pt-3 border-t border-[#E5E5E5] grid grid-cols-4 gap-2 text-[10px]">
        <CapBox label="Green (<3%)"   cap={data.config.cap_green}   isActive={data.band === "green"} />
        <CapBox label="Amber (3-5%)"  cap={data.config.cap_amber}   isActive={data.band === "amber"} />
        <CapBox label="Red (>5%)"     cap={0}                         isActive={data.band === "red"} red />
        <CapBox label="Warm-up"       cap={data.config.cap_starter}  isActive={data.band === "starter"} />
      </div>
      {data.config.hard_ceiling != null && (
        <p className="text-[10px] text-[#A3A3A3] mt-2">
          Hard operator ceiling in effect: <code>DRIP_DAILY_CAP={data.config.hard_ceiling}</code> —
          clamps any band-derived cap to this maximum. Unset / raise via env to let the band rule.
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// RepeatIncidentBanner — tenant-level repeat-incident detector. Renders
// at the top of /marketing/workflows when the cap has needed manual
// override ≥ CAP_OVERRIDE_REPEAT_INCIDENT_THRESHOLD times in the last
// CAP_OVERRIDE_REPEAT_INCIDENT_DAYS days. The signal is tenant-level
// (not per-workflow) because the adaptive cap itself is global across
// all workflows + CRM drip — pinning incidents to one workflow would
// be a misleading heuristic.
//
// Self-hides when the count is below threshold. Polled once on mount.
// ═══════════════════════════════════════════════════════════════════════
export function RepeatIncidentBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/marketing/cap-override/incident-summary")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.is_repeat_incident) return null;

  const count = data.incident_count;
  const threshold = data.threshold;
  const windowDays = data.window_days;
  const breakdown = data.breakdown || {};
  const latestIso = data.latest_incident_at;
  const latestStr = latestIso
    ? formatLocalDateTz(latestIso)
    : null;

  return (
    <div className="border-2 border-[#DC2626] bg-[#FEF2F2] p-4 mb-4"
         data-testid="repeat-incident-banner">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#991B1B]">
          ⚠️ Repeat cap-override incidents · {count}× in {windowDays}d
        </p>
        <span className="text-[10px] text-[#525252] tabular-nums">
          threshold for alert: {threshold}× / {windowDays}d
        </span>
      </div>
      <p className="text-sm text-[#0A0A0A] leading-snug mt-1">
        The system has needed manual cap intervention <strong>{count} times</strong> in
        the last {windowDays} days{latestStr ? <>, most recently on <strong>{latestStr}</strong></> : null}.
        That's a tenant-level signal that ride-the-override isn't fixing
        the underlying deliverability problem — review your overall list
        hygiene and high-volume workflows.
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#525252] tabular-nums">
        <span data-testid="repeat-incident-active">
          <strong>Currently active:</strong> {breakdown.active ?? 0}
        </span>
        <span data-testid="repeat-incident-auto">
          <strong>Auto-cleared:</strong> {breakdown.auto_cleared ?? 0}
        </span>
        <span data-testid="repeat-incident-manual">
          <strong>Manually cleared:</strong> {breakdown.manually_cleared ?? 0}
        </span>
        <span data-testid="repeat-incident-replaced">
          <strong>Replaced:</strong> {breakdown.replaced ?? 0}
        </span>
      </div>
      <p className="text-[10px] text-[#A3A3A3] mt-2 leading-relaxed">
        Note: the adaptive cap is global across all workflows and CRM drip,
        so this is a tenant-level signal — open any workflow's Analytics
        tab to see the per-incident history.
      </p>
    </div>
  );
}


function CapBox({ label, cap, isActive, red }) {
  return (
    <div className={`p-2 ${isActive ? "bg-white border" : "opacity-60"}`}
         style={isActive ? { borderColor: red ? "#DC2626" : "#16A34A" } : {}}>
      <div className="text-[#A3A3A3] uppercase tracking-wider">{label}</div>
      <div className="text-sm font-bold tabular-nums text-[#0A0A0A]">{cap} /day</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CapOverrideHistory — operator-history tile that sits below
// AdaptiveCapStatus on the Analytics tab. Shows the last 5 cap-override
// rows with a coloured badge for each clear-type so spotting patterns
// is glanceable: "Last time bounce rates spiked, Mike released 20/day
// for 9 days and it auto-cleared."
//
// Self-hides when there are zero rows so the tile doesn't add noise to
// tenants that have never touched the override.
// ═══════════════════════════════════════════════════════════════════════
export function CapOverrideHistory({ limit = 5 }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/marketing/cap-override/history", { params: { limit } })
      .then(r => { if (!cancelled) setRows(r.data?.history || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [limit]);

  if (!rows || rows.length === 0) return null;

  const fmtDuration = (sec) => {
    if (sec == null) return "—";
    const days = Math.floor(sec / 86400);
    const hours = Math.floor((sec % 86400) / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m`;
    return `${sec}s`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return formatLocalTz(iso, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", withZoneName: true,
    });
  };

  const badgeStyle = (clearType) => {
    switch (clearType) {
      case "active":   return { bg: "#DBEAFE", fg: "#1E3A8A", label: "Active" };
      case "manual":   return { bg: "#F5F5F5", fg: "#525252", label: "Manual" };
      case "auto":     return { bg: "#DCFCE7", fg: "#166534", label: "Auto" };
      case "replaced": return { bg: "#FEF3C7", fg: "#92400E", label: "Replaced" };
      default:         return { bg: "#F5F5F5", fg: "#525252", label: "—" };
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] mt-3"
         data-testid="cap-override-history">
      <div className="px-4 py-2.5 border-b border-[#E5E5E5] flex items-baseline justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
          Recent override history
        </h3>
        <span className="text-[10px] text-[#A3A3A3]">
          last {rows.length} {rows.length === 1 ? "override" : "overrides"}
        </span>
      </div>
      <table className="w-full text-[11px]">
        <thead className="bg-[#FAFAFA] text-[#525252] uppercase tracking-wider text-[10px]">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">When</th>
            <th className="text-left px-3 py-2 font-semibold">By</th>
            <th className="text-right px-3 py-2 font-semibold">Cap</th>
            <th className="text-right px-3 py-2 font-semibold">Duration</th>
            <th className="text-left px-3 py-2 font-semibold">Cleared by</th>
            <th className="text-left px-3 py-2 font-semibold">Type</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const b = badgeStyle(r.clear_type);
            return (
              <tr key={r.id} className="border-t border-[#F5F5F5]"
                  data-testid={`cap-override-row-${r.id}`}>
                <td className="px-3 py-2 text-[#0A0A0A] tabular-nums whitespace-nowrap"
                    title={r.created_at}>
                  {fmtDate(r.created_at)}
                </td>
                <td className="px-3 py-2 text-[#0A0A0A]"
                    title={r.reason || ""}>
                  {r.created_by_email || "—"}
                  {r.reason && (
                    <span className="ml-1 text-[#A3A3A3]" title={r.reason}>
                      · note
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#0A0A0A]">
                  {r.daily_cap}/day
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-[#525252]">
                  {fmtDuration(r.duration_seconds)}
                </td>
                <td className="px-3 py-2 text-[#525252]"
                    title={r.cancelled_reason || ""}>
                  {r.clear_type === "active"
                    ? <span className="italic text-[#A3A3A3]">currently running</span>
                    : (r.cancelled_by_email || "—")}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    style={{ background: b.bg, color: b.fg }}
                    data-testid={`cap-override-badge-${r.clear_type}`}
                  >
                    {b.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t border-[#F5F5F5] text-[10px] text-[#A3A3A3] leading-relaxed">
        <strong>Manual</strong> = operator clicked "clear override".
        <strong className="ml-2">Auto</strong> = bounce rate recovered to green ≥7 days after override was set.
        <strong className="ml-2">Replaced</strong> = a newer override superseded this one.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CapPauseBanner — compact global banner that surfaces the adaptive-cap
// throttle WHERE operators actually land (top of workflow detail + top of
// workflows list). Separate from the per-workflow `wf.kill_switch` banner
// because a red-band adaptive cap silently throttles sends to 0/day
// WITHOUT setting `kill_switch`. Uses the same `/marketing/cap-status`
// endpoint as the detailed `AdaptiveCapStatus` card on the Analytics tab.
//
// Surfaces the operator-set manual override (band='override') in blue
// with a "Clear override" link. When the band is red/amber and there's
// no active override, exposes an "Override cap" button that opens a
// small dialog asking how many sends per day to release.
//
// Silent when band is green/starter (healthy) AND no override is in
// effect. `compact` mode renders a single-line pill for the list page;
// default mode is a multi-line banner for the detail page.
// ═══════════════════════════════════════════════════════════════════════
export function CapPauseBanner({ compact = false }) {
  const [data, setData] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [draftCap, setDraftCap] = useState("20");
  const [draftReason, setDraftReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reload = () => {
    api.get("/marketing/cap-status")
      .then(r => setData(r.data))
      .catch(() => setData(null));
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/marketing/cap-status")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  // Silent when band is healthy AND no operator override is in effect.
  const isOverride = data.band === "override";
  const isThrottled = data.band === "red" || data.band === "amber";
  if (!isThrottled && !isOverride) return null;

  const isRed = data.band === "red";
  const bg     = isOverride ? "#EFF6FF" : isRed ? "#FEF2F2" : "#FFFBEB";
  const border = isOverride ? "#0055FF" : isRed ? "#DC2626" : "#F59E0B";
  const fg     = isOverride ? "#1E3A8A" : isRed ? "#991B1B" : "#92400E";
  const title = isOverride
    ? `Manual override active — ${data.cap} sends/day`
    : isRed
      ? "Outbound paused — adaptive cap (red band)"
      : "Outbound throttled — adaptive cap (amber band)";
  const ratePct = (data.rate_7d * 100).toFixed(2);

  const submitOverride = async () => {
    setError(null);
    const n = parseInt(draftCap, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10000) {
      setError("Enter a whole number between 1 and 10000.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/marketing/cap-override", {
        daily_cap: n,
        reason: draftReason.trim() || null,
      });
      setData(r.data);
      setShowDialog(false);
      setDraftReason("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to set override.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm(
      "Clear the manual override and let the adaptive band resume?"
    )) return;
    setSubmitting(true);
    try {
      const r = await api.delete("/marketing/cap-override");
      setData(r.data);
    } catch {
      // Silently fail — the next reload will reflect actual state.
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  const OverrideDialog = showDialog && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         data-testid="cap-override-dialog">
      <div className="bg-white border border-[#0A0A0A] w-full max-w-md p-5">
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">
          Override the adaptive cap
        </h3>
        <p className="text-[11px] text-[#525252] leading-relaxed mb-4">
          The system is currently <strong style={{ color: border }}>{data.band}</strong> due
          to a 7-day bounce rate of {ratePct}%. Setting an override
          will release the chosen number of sends per day until you
          manually clear it. Sends are still paced by the 60-second
          per-send gap so they will not all blast at once.
        </p>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1">
          Sends per day
        </label>
        <input
          type="number"
          min="1"
          max="10000"
          step="1"
          value={draftCap}
          onChange={e => setDraftCap(e.target.value)}
          className="w-full border border-[#A3A3A3] px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-[#0055FF]"
          data-testid="cap-override-cap-input"
          autoFocus
        />
        <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1 mt-3">
          Reason (optional)
        </label>
        <input
          type="text"
          maxLength={500}
          value={draftReason}
          onChange={e => setDraftReason(e.target.value)}
          placeholder="e.g. Releasing 20/day until we re-validate the bounce list"
          className="w-full border border-[#A3A3A3] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
          data-testid="cap-override-reason-input"
        />
        {error && (
          <p className="text-[11px] text-[#DC2626] mt-2" data-testid="cap-override-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => { setShowDialog(false); setError(null); }}
            disabled={submitting}
            className="px-3 py-1.5 text-xs text-[#525252] hover:text-[#0A0A0A]"
            data-testid="cap-override-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submitOverride}
            disabled={submitting}
            className="bg-[#0055FF] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#0040CC] disabled:opacity-50"
            data-testid="cap-override-confirm-btn"
          >
            {submitting ? "Saving…" : "Set override"}
          </button>
        </div>
      </div>
    </div>
  );

  if (compact) {
    return (
      <>
        <div className="border px-3 py-2 mb-3 flex items-center gap-2 text-[11px]"
             style={{ background: bg, borderColor: border, color: fg }}
             data-testid="cap-pause-banner-compact">
          <span className="font-bold uppercase tracking-wider">
            {isOverride ? "Override" : isRed ? "Paused" : "Throttled"}
          </span>
          <span>
            {isOverride
              ? <>Releasing <strong className="tabular-nums">{data.cap}/day</strong> · 7d bounce rate {ratePct}% ({data.bounced_7d}/{data.sent_7d})</>
              : <>7d bounce rate <strong className="tabular-nums">{ratePct}%</strong> ({data.bounced_7d}/{data.sent_7d}) · today's cap <strong className="tabular-nums">{data.cap}/day</strong></>
            }
          </span>
          {isOverride ? (
            <button onClick={clearOverride} disabled={submitting}
                    className="ml-auto underline hover:no-underline disabled:opacity-50"
                    data-testid="cap-override-clear-btn-compact">
              clear override
            </button>
          ) : (
            <button onClick={() => setShowDialog(true)}
                    className="ml-auto underline hover:no-underline"
                    data-testid="cap-override-open-btn-compact">
              override cap
            </button>
          )}
        </div>
        {OverrideDialog}
      </>
    );
  }

  return (
    <>
      <div className="border p-4 mb-4" style={{ background: bg, borderColor: border }}
           data-testid="cap-pause-banner">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: border }}>
            {isOverride ? "▶" : isRed ? "⏸" : "⚠️"} {title}
          </p>
          <span className="text-[10px] text-[#525252] tabular-nums">
            today's effective cap: <strong style={{ color: border }}>{data.cap} sends/day</strong>
          </span>
        </div>
        <p className="text-sm text-[#0A0A0A] leading-snug">{data.reason}</p>
        <div className="flex items-center gap-2 mt-2.5">
          {isOverride ? (
            <>
              <button
                onClick={clearOverride}
                disabled={submitting}
                className="bg-white border border-[#0055FF] text-[#0055FF] px-3 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0055FF] hover:text-white disabled:opacity-50"
                data-testid="cap-override-clear-btn"
              >
                Clear override
              </button>
              <span className="text-[11px] text-[#525252]">
                Restores the adaptive band (currently <strong>{data.underlying?.band}</strong>).
              </span>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowDialog(true)}
                className="bg-[#0055FF] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0040CC]"
                data-testid="cap-override-open-btn"
              >
                Override cap
              </button>
              <span className="text-[11px] text-[#525252]">
                Sends resume automatically once the 7-day bounce rate drops below threshold, OR set a manual override now.
              </span>
            </>
          )}
        </div>
      </div>
      {OverrideDialog}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Bounce-rate trend chart (Analytics tab)
// ═══════════════════════════════════════════════════════════════════════
export function BounceTrendChart({ workflowId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/marketing/workflows/${workflowId}/bounce-trend`, { params: { days } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId, days]);

  if (loading) return <div className="bg-white border border-[#E5E5E5] p-6 text-xs text-[#A3A3A3]">Loading trend…</div>;
  if (!data) return <div className="bg-white border border-[#E5E5E5] p-6 text-xs text-[#DC2626]">Bounce trend unavailable.</div>;

  const chartData = data.trend.map(t => ({
    date: t.date.slice(5),             // MM-DD for axis brevity
    bounce_pct: +(t.bounce_rate * 100).toFixed(2),
    sent: t.sent,
    bounced: t.bounced,
    band: t.band,
  }));
  const bandColor = (b) => b === "red" ? "#DC2626" : b === "amber" ? "#F59E0B" : "#16A34A";
  const rolling = data.rolling_7_day;
  const rollingPct = +(rolling.bounce_rate * 100).toFixed(2);

  return (
    <div className="bg-white border border-[#E5E5E5] mt-3" data-testid="wf-bounce-trend">
      <div className="px-4 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
          Bounce-rate trend · rolling {data.days} days
        </h3>
        <div className="flex items-center gap-3 text-[10px] text-[#A3A3A3]">
          <span>
            7-day rolling:
            <strong className="ml-1 tabular-nums" style={{ color: bandColor(rolling.band) }}>
              {rollingPct}%
            </strong>
            <span className="ml-1">({rolling.bounced}/{rolling.sent})</span>
          </span>
          <select value={days} onChange={e => setDays(+e.target.value)}
                  className="border border-[#E5E5E5] h-6 px-2 text-[10px]"
                  data-testid="bounce-trend-days">
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
            <option value={60}>60d</option>
          </select>
        </div>
      </div>
      <div className="p-3" style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
            <CartesianGrid stroke="#F5F5F5" vertical={false} />
            {/* Deliverability threshold bands: 0-3% green, 3-5% amber, 5%+ red */}
            <ReferenceArea y1={0} y2={3} fill="#16A34A" fillOpacity={0.04} ifOverflow="extendDomain" />
            <ReferenceArea y1={3} y2={5} fill="#F59E0B" fillOpacity={0.08} ifOverflow="extendDomain" />
            <ReferenceArea y1={5} y2={100} fill="#DC2626" fillOpacity={0.08} ifOverflow="extendDomain" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#A3A3A3" }} />
            <YAxis tick={{ fontSize: 10, fill: "#A3A3A3" }}
                   tickFormatter={v => `${v}%`} width={38} />
            <ReTooltip
              cursor={{ fill: "#F5F5F5" }}
              contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", padding: "4px 8px" }}
              formatter={(value, name, item) => [
                `${value}% (${item?.payload?.bounced}/${item?.payload?.sent})`,
                "Bounce rate",
              ]}
            />
            <Bar dataKey="bounce_pct">
              {chartData.map((d, i) => (
                <Cell key={i} fill={bandColor(d.band)} />
              ))}
              <LabelList dataKey="bounce_pct" position="top"
                         formatter={(v) => v > 0 ? `${v}%` : ""}
                         style={{ fontSize: 9, fill: "#525252" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="px-4 py-2 border-t border-[#E5E5E5] bg-[#FAFAFA] flex items-center gap-4 text-[10px] text-[#A3A3A3]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#16A34A]" /> &lt; 3% healthy</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#F59E0B]" /> 3-5% caution</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626]" /> &gt; 5% deliverability risk</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Clicks tab — heat-map first-class + full contact table
// ═══════════════════════════════════════════════════════════════════════
export function ClicksTab({ workflowId }) {
  const [loading, setLoading] = useState(true);
  const [clickMap, setClickMap] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [urlFilter, setUrlFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/marketing/workflows/${workflowId}/click-map`),
      api.get(`/marketing/workflows/${workflowId}/event-contacts`,
              { params: { event_type: "clicked", limit: 5000 } }),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setClickMap(a.data);
        setContacts(b.data?.contacts || []);
      })
      .catch(e => {
        const msg = e?.response?.data?.detail || e?.message || "Failed to load";
        toast.error(`Could not load clicks: ${msg}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId]);

  const filtered = useMemo(() => {
    return contacts.filter(c => {
      if (urlFilter && (c.url || "") !== urlFilter) return false;
      if (search) {
        const blob = `${c.email || ""} ${c.full_name || ""} ${c.company_name || ""} ${c.url || ""}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [contacts, urlFilter, search]);

  const exportCsv = () => {
    const header = ["email", "name", "company", "url", "variant", "step", "device_client", "when"];
    const rows = filtered.map(c => [
      c.email || "", c.full_name || "",
      c.company_name || "", c.url || "",
      c.variant_id || "", c.workflow_step_index ?? "",
      `${c.device || ""} · ${c.client || ""}`, c.occurred_at || "",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clicks-${workflowId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (loading) return <div className="bg-white border border-[#E5E5E5] p-6 text-xs text-[#A3A3A3]">Loading clicks…</div>;

  return (
    <div className="space-y-4" data-testid="wf-clicks-tab">
      {/* HEAT-MAP — big and front-and-centre */}
      {clickMap && clickMap.urls?.length > 0 ? (
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-baseline justify-between">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
              Per-URL heat-map
            </h3>
            <span className="text-[10px] text-[#A3A3A3]">
              {clickMap.total_clicks} clicks · {clickMap.unique_clickers} unique clickers · {clickMap.urls.length} distinct URLs
            </span>
          </div>
          <div className="p-4 space-y-2">
            {clickMap.urls.map((u, i) => {
              const max = Math.max(...clickMap.urls.map(x => x.pct_of_clicks), 1);
              const widthPct = Math.max(2, (u.pct_of_clicks / max) * 100);
              const intensity = Math.max(0.35, 1 - (i * 0.08));
              const isActive = urlFilter === u.url;
              return (
                <button
                  key={u.url}
                  type="button"
                  onClick={() => setUrlFilter(isActive ? "" : u.url)}
                  className={`w-full flex items-center gap-3 text-[11px] group ${isActive ? "ring-2 ring-[#7C3AED]" : ""}`}
                  data-testid={`clicks-tab-heat-row-${i}`}
                  title={isActive ? "Clear filter" : "Filter table by this URL"}
                >
                  <span className="w-[56px] text-right font-bold tabular-nums text-[#171717]">
                    {u.pct_of_clicks.toFixed(1)}%
                  </span>
                  <div className="flex-1 relative h-6 bg-[#F5F5F5]">
                    <div className="absolute inset-y-0 left-0 transition-all duration-300"
                         style={{ width: `${widthPct}%`, background: `rgba(124,58,237,${intensity})` }} />
                    <div className="absolute inset-0 flex items-center px-2">
                      <span className="text-[11px] text-[#171717] truncate max-w-full text-left">
                        {u.url}
                      </span>
                    </div>
                  </div>
                  <span className="w-[130px] text-right text-[10px] text-[#525252] tabular-nums">
                    {u.clicks} clicks · {u.unique_contacts} ppl
                  </span>
                </button>
              );
            })}
          </div>
          <div className="px-4 py-2 border-t border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between text-[10px] text-[#A3A3A3]">
            <span>Tip: click any bar to filter the contacts table below.</span>
            {urlFilter && (
              <button onClick={() => setUrlFilter("")}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] hover:underline"
                      data-testid="clicks-tab-clear-filter">
                Clear filter
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] p-6 text-center text-sm text-[#A3A3A3]">
          No clicks on this workflow yet.
        </div>
      )}

      {/* CONTACTS TABLE */}
      {contacts.length > 0 && (
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center gap-3">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] flex-shrink-0">
              Every click · {filtered.length}
              {filtered.length !== contacts.length && <span className="text-[10px] text-[#A3A3A3] font-normal ml-1">of {contacts.length}</span>}
            </h3>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search email / name / company / url…"
              className="flex-1 border border-[#E5E5E5] h-8 px-2 text-xs"
              data-testid="clicks-tab-search"
            />
            <button onClick={exportCsv}
                    disabled={filtered.length === 0}
                    className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#F5F5F5] disabled:opacity-40"
                    data-testid="clicks-tab-csv">
              Export CSV
            </button>
          </div>
          <div className="overflow-auto max-h-[520px]">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3] sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Contact</th>
                  <th className="text-left px-3 py-2 font-bold">Company</th>
                  <th className="text-left px-3 py-2 font-bold">URL</th>
                  <th className="text-left px-3 py-2 font-bold">Variant · Step</th>
                  <th className="text-left px-3 py-2 font-bold">Device · Client</th>
                  <th className="text-right px-4 py-2 font-bold">When</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={i} className="border-t border-[#F5F5F5]"
                      data-testid={`clicks-tab-row-${i}`}>
                    <td className="px-4 py-2">
                      <div className="text-[#0A0A0A]">{c.full_name || "—"}</div>
                      <div className="text-[10px] text-[#A3A3A3]">{c.email || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-[#525252]">{c.company_name || ""}</td>
                    <td className="px-3 py-2 max-w-[280px]">
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noopener noreferrer"
                           className="text-[#7C3AED] hover:underline truncate block"
                           title={c.url}>{c.url}</a>
                      ) : ""}
                    </td>
                    <td className="px-3 py-2 text-[#525252] tabular-nums">
                      {c.variant_id || "—"} · {c.workflow_step_index ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-[#525252]">
                      {(c.device || "") + (c.client ? ` · ${c.client}` : "")}
                    </td>
                    <td className="px-4 py-2 text-right text-[10px] text-[#A3A3A3] tabular-nums">
                      {formatLocal(c.occurred_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Enrollments tab — 3-bucket classification + filters + search
// ═══════════════════════════════════════════════════════════════════════
const BUCKET_META = {
  contacted:  { label: "Contacted",   color: "#16A34A", help: "At least one email has gone out to this contact." },
  in_queue:   { label: "In queue",    color: "#F59E0B", help: "Eligible to send NOW but waiting behind the 60s throttle + 20/day cap." },
  scheduled:  { label: "Scheduled",   color: "#6B7280", help: "No email yet; next_action_at is in the future — will enter the queue later." },
  completed:  { label: "Completed",   color: "#0055FF", help: "Workflow finished for this contact." },
  errored:    { label: "Errored",     color: "#DC2626", help: "Send errored — manual review required." },
  stalled:    { label: "Stalled",     color: "#92400E", help: "Awaiting variant winner before continuing." },
  paused:     { label: "Paused",      color: "#525252", help: "Enrollment paused manually." },
};

export function EnrollmentsTab({ enrollments }) {
  const [bucketFilter, setBucketFilter] = useState("all");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const c = {};
    for (const e of enrollments) {
      const b = e.status_bucket || e.status || "—";
      c[b] = (c[b] || 0) + 1;
    }
    return c;
  }, [enrollments]);

  const filtered = useMemo(() => {
    // Bucket-priority ordering: in_queue first (most actionable), then
    // scheduled (upcoming), then contacted (history), then completed.
    // Within each bucket sort by the most relevant timestamp.
    const bucketRank = {
      in_queue: 0, scheduled: 1, contacted: 2, completed: 3,
      stalled: 4, paused: 5, errored: 6,
    };
    const ts = (s) => s ? new Date(asUtc(s)).getTime() : Infinity;
    const sorted = [...enrollments].sort((a, b) => {
      const ra = bucketRank[a.status_bucket] ?? 99;
      const rb = bucketRank[b.status_bucket] ?? 99;
      if (ra !== rb) return ra - rb;
      // Within in_queue / scheduled: oldest next_action_at first
      // (matches executor FIFO — what gets sent next).
      if (a.status_bucket === "in_queue" || a.status_bucket === "scheduled") {
        return ts(a.next_action_at) - ts(b.next_action_at);
      }
      // Within contacted: most-recent last_action_at first
      // (operator wants to see what just went out).
      if (a.status_bucket === "contacted") {
        return ts(b.last_action_at) - ts(a.last_action_at);
      }
      // Fallback: enrolled_at DESC
      return ts(b.enrolled_at) - ts(a.enrolled_at);
    });
    return sorted.filter(e => {
      if (bucketFilter !== "all" && (e.status_bucket || "") !== bucketFilter) return false;
      if (search) {
        const blob = `${e.email || ""} ${e.full_name || ""} ${e.last_action_subject || ""}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [enrollments, bucketFilter, search]);

  const exportCsv = () => {
    const header = ["email", "name", "bucket", "queue_position", "estimated_send_day_offset",
                    "last_action_at", "last_action_subject",
                    "next_action_at", "next_action_subject",
                    "enrolled_at", "current_step_index"];
    const rows = filtered.map(e => [
      e.email || "", e.full_name || "",
      e.status_bucket || "", e.queue_position ?? "", e.estimated_send_offset_days ?? "",
      e.last_action_at || "", e.last_action_subject || "",
      e.next_action_at || "", e.next_action_subject || "",
      e.enrolled_at || "", e.current_step_index ?? "",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `enrollments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="wf-enrollments-tab">
      <div className="px-4 py-3 border-b border-[#E5E5E5] space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
            Enrolled contacts — {enrollments.length}
          </h2>
          <button onClick={exportCsv}
                  disabled={filtered.length === 0}
                  className="h-7 px-3 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#F5F5F5] disabled:opacity-40"
                  data-testid="enrollments-csv-export">
            Export CSV
          </button>
        </div>
        {/* Bucket filter chips — also double as summary tiles */}
        <div className="flex flex-wrap gap-2">
          <BucketChip active={bucketFilter === "all"} onClick={() => setBucketFilter("all")}
                      color="#171717" label="All" count={enrollments.length} tid="bucket-all" />
          {Object.keys(BUCKET_META).map(k => counts[k] ? (
            <BucketChip key={k}
                        active={bucketFilter === k}
                        onClick={() => setBucketFilter(k)}
                        color={BUCKET_META[k].color}
                        label={BUCKET_META[k].label}
                        count={counts[k]}
                        title={BUCKET_META[k].help}
                        tid={`bucket-${k}`} />
          ) : null)}
        </div>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search by email, name, or subject…"
               className="w-full border border-[#E5E5E5] h-8 px-2 text-xs"
               data-testid="enrollments-search" />
      </div>
      {filtered.length === 0 ? (
        <p className="p-6 text-sm text-[#A3A3A3] text-center">No contacts match the current filter.</p>
      ) : (
        <div className="overflow-auto max-h-[620px]">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Contact</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Last contact</th>
                <th className="text-left px-3 py-2 font-bold">Next contact</th>
                <th className="text-right px-4 py-2 font-bold">Enrolled</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => <EnrollmentRow key={e.id} e={e} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BucketChip({ active, onClick, color, label, count, title, tid }) {
  return (
    <button type="button" onClick={onClick} title={title}
            data-testid={tid}
            className={`flex items-center gap-2 h-7 px-2.5 border text-[10px] font-bold uppercase tracking-wider transition-colors ${
              active
                ? "text-white border-transparent"
                : "text-[#525252] bg-white border-[#E5E5E5] hover:bg-[#F5F5F5]"
            }`}
            style={active ? { backgroundColor: color } : {}}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : ""}`}
            style={active ? {} : { backgroundColor: color }} />
      {label}
      <span className={`tabular-nums ${active ? "opacity-90" : "text-[#A3A3A3]"}`}>{count}</span>
    </button>
  );
}

function EnrollmentRow({ e }) {
  const bucket = e.status_bucket || "";
  const meta = BUCKET_META[bucket];
  const offsetDays = e.estimated_send_offset_days ?? 0;
  return (
    <tr className="border-t border-[#F5F5F5]" data-testid={`enroll-row-${e.id}`}>
      <td className="px-4 py-2.5">
        <div className="text-[#0A0A0A]">{e.full_name || e.email || e.contact_id}</div>
        {e.full_name && e.email && (
          <div className="text-[10px] text-[#A3A3A3]">{e.email}</div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {meta ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                style={{ color: meta.color }} title={meta.help}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
            {meta.label}
          </span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-wider">{e.status}</span>
        )}
        <div className="text-[10px] text-[#A3A3A3] tabular-nums">step {e.current_step_index}</div>
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap"
          data-testid={`enroll-row-last-action-${e.id}`}>
        {e.last_action_at ? (
          <>
            <div className="text-[#525252] tabular-nums">{formatLocal(e.last_action_at)}</div>
            {e.last_action_subject && (
              <div className="text-[10px] text-[#A3A3A3] max-w-[280px] truncate" title={e.last_action_subject}>
                “{e.last_action_subject}”
              </div>
            )}
          </>
        ) : bucket === "in_queue" ? (
          <div>
            <div className="text-[11px] text-[#92400E] font-medium">
              Queue position #{e.queue_position ?? "—"}
            </div>
            <div className="text-[10px] text-[#A3A3A3]">
              {offsetDays === 0
                ? "Due today (if cap allows)"
                : `Est. send in ~${offsetDays}d`}
            </div>
          </div>
        ) : bucket === "scheduled" ? (
          <span className="text-[10px] text-[#6B7280] italic">
            Not yet in queue
          </span>
        ) : (
          <span className="text-[10px] text-[#A3A3A3] italic">No email sent yet</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap"
          data-testid={`enroll-row-next-action-${e.id}`}>
        <div className={e.next_action_overdue ? "text-[#92400E] font-medium" : "text-[#525252]"}>
          {e.next_action_label || "—"}
        </div>
        {e.next_action_at && (
          <div className="text-[10px] text-[#A3A3A3] tabular-nums">
            {e.next_action_overdue ? "due " : ""}{formatLocal(e.next_action_at)}
          </div>
        )}
        {e.next_action_subject && (
          <div className="text-[10px] text-[#A3A3A3] max-w-[280px] truncate" title={e.next_action_subject}>
            “{e.next_action_subject}”
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right text-xs text-[#A3A3A3]">
        {e.enrolled_at ? formatLocalDate(e.enrolled_at) : ""}
      </td>
    </tr>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Engagement Follow-Up tab (Session 63 — WYSIWYG editor + clearer activity)
// ═══════════════════════════════════════════════════════════════════════
//
// Per-workflow editable template that auto-sends a follow-up email
// `delay_hours` after a contact clicks any link in any workflow email.
// Suppressions: replied / unsubscribed / bounced / demo_booked_date set.
// Send-window: respects the same Mon-Thu 09:30-12:00, Fri 09:30-11:00
// recipient-local rule as the workflow executor.
export function FollowUpTab({ workflowId }) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [logFilter, setLogFilter] = useState("active"); // active | sent | scheduled | suppressed | all
  const [showCancelled, setShowCancelled] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [tplR, logR] = await Promise.all([
        api.get(`/marketing/workflows/${workflowId}/engagement-followup`),
        api.get(`/marketing/workflows/${workflowId}/engagement-followup/log?limit=100&include_cancelled=${showCancelled}`),
      ]);
      setTpl(tplR.data);
      setLogRows(logR.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load follow-up template");
    }
    setLoading(false);
  };
  useEffect(() => { if (workflowId) reload(); /* eslint-disable-next-line */ }, [workflowId, showCancelled]);

  const update = (patch) => setTpl({ ...tpl, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(
        `/marketing/workflows/${workflowId}/engagement-followup`,
        {
          subject: tpl.subject,
          body_html: tpl.body_html,
          enabled: tpl.enabled,
          delay_hours: parseInt(tpl.delay_hours, 10) || 24,
        },
      );
      setTpl(r.data);
      toast.success(tpl.enabled ? "Saved · follow-ups are LIVE" : "Saved (still disabled)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const showPreview = async () => {
    try {
      const r = await api.get(`/marketing/workflows/${workflowId}/engagement-followup/preview`);
      setPreviewHtml(r.data.html);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const testSend = async () => {
    const to = (testEmail || "").trim();
    if (!to) { toast.error("Enter an email"); return; }
    try {
      await api.post(`/marketing/workflows/${workflowId}/engagement-followup/test-send`, { to_email: to });
      toast.success(`Test follow-up sent to ${to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    }
  };

  if (loading || !tpl) {
    return <div className="text-xs text-[#A3A3A3] p-4" data-testid="followup-loading">Loading…</div>;
  }

  const stats = tpl.stats || {};

  // Filter the log rows for the activity table.
  // "Active" = currently scheduled (in flight). Suppressed/sent/cancelled
  // are terminal states and shown via their dedicated tabs.
  const filteredRows = logRows.filter(r => {
    if (logFilter === "all") return true;
    if (logFilter === "active") return r.status === "scheduled";
    if (logFilter === "sent") return r.status === "sent";
    if (logFilter === "scheduled") return r.status === "scheduled";
    if (logFilter === "suppressed") return r.status?.startsWith("suppressed_");
    return true;
  });

  return (
    <div className="space-y-4" data-testid="followup-tab">
      {/* How it works callout */}
      <div className="bg-[#EFF6FF] border border-[#0055FF] px-4 py-3" data-testid="followup-howitworks">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-1">
          How it works
        </p>
        <p className="text-xs text-[#0A0A0A] leading-relaxed">
          When a contact clicks the <strong>booking link</strong> (configured in
          Marketing Settings → Conversion URLs) but doesn't actually book, we
          automatically schedule this follow-up to send <strong>{tpl.delay_hours}h later</strong> in their local time
          window (Mon-Thu 09:30-12:00, Fri 09:30-11:00). One follow-up per contact
          per workflow. Suppressed if they reply, unsubscribe, hard-bounce, or you set a
          "Demo Booked" date on their lead. Only fires when the workflow is <strong>active</strong>.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="followup-stats">
        <StatPill label="Total" value={stats.total || 0} testid="followup-stat-total" />
        <StatPill label="Scheduled" value={stats.scheduled || 0} color="amber" testid="followup-stat-scheduled" />
        <StatPill label="Sent" value={stats.sent || 0} color="green" testid="followup-stat-sent" />
        <StatPill label="Suppressed" value={stats.suppressed || 0} color="grey" testid="followup-stat-suppressed" />
        <StatPill label="Failed" value={stats.failed || 0} color="red" testid="followup-stat-failed" />
      </div>

      {/* Editor card */}
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
            Follow-Up Email Template
          </h2>
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="followup-enable-toggle">
            <input
              type="checkbox"
              checked={!!tpl.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
              className="w-4 h-4 accent-[#0055FF]"
            />
            <span className={`text-xs font-bold uppercase tracking-wider ${tpl.enabled ? "text-[#16A34A]" : "text-[#525252]"}`}>
              {tpl.enabled ? "Enabled · sending live" : "Disabled"}
            </span>
          </label>
        </div>

        {/* Delay control */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#F5F5F5]">
          <label className="text-xs text-[#525252]">Send</label>
          <input
            type="number"
            min={1}
            max={168}
            value={tpl.delay_hours}
            onChange={(e) => update({ delay_hours: parseInt(e.target.value || "24", 10) })}
            className="h-8 w-20 px-2 text-xs border border-[#E5E5E5] tabular-nums"
            data-testid="followup-delay-hours"
          />
          <span className="text-xs text-[#525252]">hours after click ·</span>
          <span className="text-[11px] text-[#A3A3A3]">
            (1-168, then deferred to next valid window slot)
          </span>
        </div>

        {/* Subject */}
        <div className="mb-3">
          <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">Subject</label>
          <input
            type="text"
            value={tpl.subject || ""}
            onChange={(e) => update({ subject: e.target.value })}
            className="w-full h-9 px-3 text-sm border border-[#E5E5E5]"
            data-testid="followup-subject-input"
            placeholder="e.g. Quick follow-up — happy to work around your schedule"
          />
        </div>

        {/* WYSIWYG body editor — same TipTap component used by the
            campaign builder + workflow steps so signatures, brand
            assets and tokens behave identically. */}
        <div className="mb-3" data-testid="followup-body-editor">
          <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
            Body
          </label>
          <RichTextEditor
            html={tpl.body_html || ""}
            onChange={(h) => update({ body_html: h })}
            placeholder="Write your follow-up email…"
            minHeight={260}
            testId="followup-body-input"
          />
        </div>

        {/* Action row */}
        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#F5F5F5]">
          <button
            onClick={save}
            disabled={saving}
            className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="followup-save-btn">
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={showPreview}
            className="h-9 px-4 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider"
            data-testid="followup-preview-btn">
            Preview
          </button>
          <div className="flex-1" />
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@eyvo.com"
            className="h-9 px-3 text-xs border border-[#E5E5E5] w-56"
            data-testid="followup-test-email-input"
          />
          <button
            onClick={testSend}
            className="h-9 px-4 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider"
            data-testid="followup-test-send-btn">
            Send test
          </button>
        </div>
        {tpl.updated_at && (
          <p className="text-[10px] text-[#A3A3A3] mt-2">
            Last edited {formatLocal(tpl.updated_at)}
          </p>
        )}
      </div>

      {/* Recent activity log — clearer header, filter pills, empty state */}
      <div className="bg-white border border-[#E5E5E5] p-4" data-testid="followup-log-section">
        <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
              Follow-Up Activity
            </h2>
            <p className="text-[11px] text-[#525252] mt-1 leading-relaxed max-w-3xl">
              Every row is <strong>one lead who clicked the booking link</strong> in
              this workflow's emails (matched against your configured
              Conversion URLs). We schedule one follow-up per lead, then either
              send it 24h later, or suppress it if the lead has already replied,
              unsubscribed, hard-bounced, or you've marked them as Booked.
            </p>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-[#525252] cursor-pointer mt-1 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="w-3 h-3 accent-[#0055FF]"
              data-testid="followup-include-cancelled"
            />
            Include cancelled
          </label>
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-1 my-3 flex-wrap" data-testid="followup-log-filters">
          {[
            { k: "active", l: "Active" },
            { k: "sent", l: "Sent" },
            { k: "suppressed", l: "Suppressed" },
            { k: "all", l: "All" },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setLogFilter(k)}
              className={`px-3 h-7 text-[11px] font-bold uppercase tracking-wider border ${
                logFilter === k
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"
              }`}
              data-testid={`followup-filter-${k}`}
            >
              {l}
            </button>
          ))}
          <span className="text-[10px] text-[#A3A3A3] ml-2 tabular-nums">
            {filteredRows.length} / {logRows.length} rows
          </span>
        </div>

        {filteredRows.length === 0 ? (
          <EmptyActivityState enabled={tpl.enabled} hasAny={logRows.length > 0} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-[#E5E5E5]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Contact</th>
                  <th className="py-2 pr-2">Triggered by</th>
                  <th className="py-2 pr-2">Scheduled for</th>
                  <th className="py-2 pr-2">Sent at</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.id} className="border-b border-[#F5F5F5]" data-testid={`followup-log-row-${r.id}`}>
                    <td className="py-2 pr-2">
                      <FollowupStatusBadge status={r.status} />
                    </td>
                    <td className="py-2 pr-2">
                      <div className="font-medium text-[#0A0A0A]">
                        {r.full_name || r.first_name || "—"}
                      </div>
                      <div className="text-[10px] text-[#A3A3A3]">{r.email}</div>
                    </td>
                    <td className="py-2 pr-2 text-[#525252]">
                      <div className="flex items-center gap-1.5 max-w-[300px]">
                        {r.is_booking_url ? (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#0055FF] whitespace-nowrap">
                            Booking link
                          </span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F5F5F5] text-[#525252] whitespace-nowrap">
                            Other link
                          </span>
                        )}
                        <span className="truncate" title={r.trigger_url}>
                          {r.trigger_url || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-[#525252] tabular-nums whitespace-nowrap">
                      {r.scheduled_for ? formatLocal(r.scheduled_for) : "—"}
                    </td>
                    <td className="py-2 pr-2 text-[#525252] tabular-nums whitespace-nowrap">
                      {r.sent_at ? formatLocal(r.sent_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             onClick={() => setPreviewHtml(null)}
             data-testid="followup-preview-modal">
          <div className="bg-white border border-[#E5E5E5] p-0 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-sm font-black tracking-tight">Preview</h3>
              <button onClick={() => setPreviewHtml(null)}
                      className="text-[#525252] hover:text-[#0A0A0A] text-xl leading-none"
                      data-testid="followup-preview-close">×</button>
            </div>
            <iframe
              srcDoc={safeSrcDoc(previewHtml)}
              title="Engagement follow-up preview"
              className="flex-1 w-full"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyActivityState({ enabled, hasAny }) {
  // Three distinct empty states so the operator knows EXACTLY why
  // they're staring at zero rows.
  if (!enabled && !hasAny) {
    return (
      <div className="text-xs text-[#525252] py-6 text-center" data-testid="followup-empty-disabled">
        <p className="font-medium">No follow-ups yet.</p>
        <p className="text-[11px] text-[#A3A3A3] mt-1">
          Once you flip the switch above to <strong>Enabled</strong>, leads who click your
          configured <strong>booking link</strong> in this workflow's emails will appear here within 5 minutes.
        </p>
      </div>
    );
  }
  if (hasAny) {
    return (
      <div className="text-xs text-[#525252] py-6 text-center" data-testid="followup-empty-filtered">
        <p className="font-medium">No rows match the current filter.</p>
        <p className="text-[11px] text-[#A3A3A3] mt-1">
          Try the <strong>All</strong> tab above to see everything in the audit trail.
        </p>
      </div>
    );
  }
  return (
    <div className="text-xs text-[#525252] py-6 text-center" data-testid="followup-empty-enabled">
      <p className="font-medium">Live and waiting.</p>
      <p className="text-[11px] text-[#A3A3A3] mt-1">
        No booking-link clicks have come in yet. As soon as someone clicks
        the configured booking URL in this workflow's emails, you'll see them here.
      </p>
    </div>
  );
}

function StatPill({ label, value, color = "blue", testid }) {
  const palette = {
    blue:  { bg: "#EFF6FF", fg: "#0055FF" },
    green: { bg: "#F0FDF4", fg: "#16A34A" },
    amber: { bg: "#FEF3C7", fg: "#92400E" },
    red:   { bg: "#FEF2F2", fg: "#DC2626" },
    grey:  { bg: "#F5F5F5", fg: "#525252" },
  }[color] || { bg: "#EFF6FF", fg: "#0055FF" };
  return (
    <div className="border border-[#E5E5E5] px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</div>
      <div className="text-2xl font-black tabular-nums" style={{ color: palette.fg }}>{value}</div>
    </div>
  );
}

function FollowupStatusBadge({ status }) {
  const map = {
    scheduled:                { bg: "#FEF3C7", fg: "#92400E", label: "Scheduled" },
    sent:                     { bg: "#F0FDF4", fg: "#16A34A", label: "Sent" },
    failed:                   { bg: "#FEF2F2", fg: "#DC2626", label: "Failed" },
    suppressed_replied:       { bg: "#F5F5F5", fg: "#525252", label: "Replied" },
    suppressed_unsub:         { bg: "#F5F5F5", fg: "#525252", label: "Unsubscribed" },
    suppressed_bounced:       { bg: "#F5F5F5", fg: "#525252", label: "Bounced" },
    suppressed_booked:        { bg: "#EFF6FF", fg: "#0055FF", label: "Booked" },
    suppressed_lead_paused:   { bg: "#F5F5F5", fg: "#525252", label: "Lead paused" },
    cancelled_disabled:       { bg: "#FEF2F2", fg: "#525252", label: "Cancelled (disabled)" },
    cancelled_seed:           { bg: "#F5F5F5", fg: "#A3A3A3", label: "Cancelled (seed)" },
  };
  const it = map[status] || { bg: "#F5F5F5", fg: "#525252", label: status || "—" };
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ backgroundColor: it.bg, color: it.fg }}>
      {it.label}
    </span>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// Manual Send tab — operator-driven N-emails-with-Y-delay burst tool
// ═══════════════════════════════════════════════════════════════════════
export function ManualSendTab({ workflowId }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [count, setCount] = useState(50);
  const [delay, setDelay] = useState(20);
  const [variantFilter, setVariantFilter] = useState("winner");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get(`/marketing/workflows/${workflowId}/manual-burst/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
    setLoadingPreview(false);
  }, [workflowId]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/marketing/workflows/${workflowId}/manual-burst/jobs?limit=10`);
      setHistory(r.data?.items || []);
      // Pick up a currently-running job if there is one — used to render
      // the live progress panel.
      const running = (r.data?.items || []).find(
        (j) => j.status === "queued" || j.status === "running"
      );
      setActiveJob(running || null);
    } catch (e) {
      /* non-fatal */
    }
  }, [workflowId]);

  useEffect(() => {
    if (workflowId) {
      loadPreview();
      loadHistory();
    }
  }, [workflowId, loadPreview, loadHistory]);

  // Poll the active job every 3 s — converges quickly without DDoSing the API.
  useEffect(() => {
    if (!activeJob) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/marketing/manual-burst/${activeJob.job_id}`);
        setActiveJob(r.data);
        if (["done", "cancelled", "errored"].includes(r.data.status)) {
          await loadHistory();
        }
      } catch { /* leave state alone */ }
    }, 3000);
    return () => clearInterval(id);
  }, [activeJob?.job_id, loadHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/marketing/workflows/${workflowId}/manual-burst`, {
        count: Number(count),
        delay_seconds: Number(delay),
        variant_filter: variantFilter,
        notes,
      });
      toast.success(`Burst queued (job ${r.data.job_id}). Estimated runtime: ${r.data.estimated_runtime_minutes} min.`);
      setConfirmOpen(false);
      setNotes("");
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start burst");
    }
    setSubmitting(false);
  };

  const cancelJob = async (jobId) => {
    const reason = window.prompt("Optional cancellation reason:", "");
    if (reason === null) return;
    try {
      await api.post(`/marketing/manual-burst/${jobId}/cancel`, { reason });
      toast.success("Cancellation requested. Will stop after the current send.");
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  if (loadingPreview) {
    return <div className="text-sm text-[#A3A3A3] p-6 text-center" data-testid="ms-loading">Loading…</div>;
  }
  if (!preview) {
    return <div className="text-sm text-[#A3A3A3] p-6 text-center">Preview unavailable.</div>;
  }

  const estMin = Math.round((Number(count) * Number(delay) / 60) * 10) / 10;
  const filterDescriptions = {
    all:        "Send to anyone due — the dispatcher's normal hash-split picks the variant.",
    winner:     "Use whichever variant has been promoted as the winner on each step.",
    variant_a:  "Only send Variant A. Skip enrolments where the natural variant pick is B or C.",
    variant_b:  "Only send Variant B. Skip enrolments where the natural variant pick is A or C.",
    variant_c:  "Only send Variant C. Skip enrolments where the natural variant pick is A or B.",
  };

  return (
    <div className="space-y-6" data-testid="manual-send-tab">
      {/* Header summary */}
      <div className="border border-[#E5E5E5] bg-white p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h3 className="text-sm font-black tracking-[0.16em] uppercase">Manual Send</h3>
          <span className="text-[10px] text-[#A3A3A3]">
            Bypasses the daily-cap throttle. Respects workflow status and step variants.
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Metric label="Active enrolments" value={preview.total_active} />
          <Metric label="Due now" value={preview.total_due_now} accent="#0055FF" />
          <Metric label="Workflow status" value={preview.workflow_status} />
          <Metric label="Email steps with variants" value={preview.variant_breakdown.length} />
        </div>
      </div>

      {/* Active job panel */}
      {activeJob && (
        <div className="border-2 border-[#0055FF] bg-[#F0F7FF] p-4" data-testid="ms-active-job">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <p className="text-sm font-black tracking-tight">
                Burst in progress &nbsp;
                <span className="text-xs text-[#0055FF] font-mono">{activeJob.job_id}</span>
              </p>
              <p className="text-xs text-[#525252] mt-1">
                {activeJob.progress_sent}/{activeJob.progress_total} sent
                {activeJob.progress_failed > 0 && <span className="text-[#DC2626] ml-2">· {activeJob.progress_failed} failed</span>}
                {activeJob.progress_skipped > 0 && <span className="text-[#525252] ml-2">· {activeJob.progress_skipped} skipped (variant filter)</span>}
                <span className="text-[#A3A3A3] ml-3">@ {activeJob.delay_seconds}s gap · filter={activeJob.variant_filter}</span>
              </p>
              <div className="mt-2 h-2 bg-white border border-[#0055FF] w-72 max-w-full">
                <div
                  className="h-full bg-[#0055FF] transition-all duration-300"
                  style={{ width: `${Math.min(100, (activeJob.progress_sent / Math.max(1, activeJob.progress_total)) * 100)}%` }}
                />
              </div>
              {/* Stalled-burst detector: if started_at is more than
                  (delay_seconds * 3 + 60s) old AND progress_sent has
                  not advanced, surface a warning so the operator
                  doesn't stare at a flat bar forever. */}
              {activeJob.started_at && (() => {
                const elapsedS = (Date.now() - new Date(activeJob.started_at).getTime()) / 1000;
                const expectedSent = Math.floor(elapsedS / Math.max(1, activeJob.delay_seconds));
                const stalled = elapsedS > (activeJob.delay_seconds * 3 + 60)
                                && activeJob.progress_sent === 0;
                const sluggish = !stalled
                                 && expectedSent >= activeJob.progress_sent + 5
                                 && elapsedS > 90;
                if (stalled) {
                  return (
                    <p className="mt-2 text-[11px] text-[#7C2D12] bg-[#FFF7ED] border border-[#FED7AA] px-2 py-1" data-testid="ms-stalled-warning">
                      ⚠ Burst hasn&apos;t sent anything in {Math.round(elapsedS)}s. Worker may have died (server restart / scale event). Cancel and re-run, or wait for the next auto-resume.
                    </p>
                  );
                }
                if (sluggish) {
                  return (
                    <p className="mt-2 text-[11px] text-[#525252]">
                      Sending slower than the configured gap — expected ~{expectedSent} by now. Check rate-limit logs.
                    </p>
                  );
                }
                return null;
              })()}
              {/* Reason note (e.g. "Stopped early — no more due enrolments") */}
              {activeJob.last_error && (
                <p className="mt-2 text-[11px] text-[#0A0A0A] bg-white border border-[#E5E5E5] px-2 py-1" data-testid="ms-job-note">
                  <span className="font-bold uppercase tracking-wider text-[10px] text-[#525252] mr-1">Note:</span>
                  {activeJob.last_error}
                </p>
              )}
            </div>
            <button
              onClick={() => cancelJob(activeJob.job_id)}
              disabled={activeJob.cancellation_requested}
              className="px-3 py-1.5 border border-[#DC2626] text-[#DC2626] text-xs font-bold uppercase tracking-wider hover:bg-[#FEF2F2] disabled:opacity-50"
              data-testid="ms-cancel-btn"
            >
              {activeJob.cancellation_requested ? "Cancelling…" : "Cancel burst"}
            </button>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="border border-[#E5E5E5] bg-white p-4">
        <h4 className="text-xs font-black uppercase tracking-[0.16em] text-[#0A0A0A] mb-3">Configure a burst</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1">
              How many emails (X)
            </label>
            <input
              type="number"
              min={1}
              max={preview.limits.max_count}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0055FF] outline-none text-sm tabular-nums"
              data-testid="ms-count-input"
            />
            <p className="text-[10px] text-[#A3A3A3] mt-1">Max {preview.limits.max_count}.</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1">
              Delay between sends (Y, seconds)
            </label>
            <input
              type="number"
              min={preview.limits.min_delay_seconds}
              max={preview.limits.max_delay_seconds}
              value={delay}
              onChange={(e) => setDelay(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0055FF] outline-none text-sm tabular-nums"
              data-testid="ms-delay-input"
            />
            <p className="text-[10px] text-[#A3A3A3] mt-1">
              Min {preview.limits.min_delay_seconds}s, max {preview.limits.max_delay_seconds}s.
            </p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1">
              Variant filter
            </label>
            <select
              value={variantFilter}
              onChange={(e) => setVariantFilter(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0055FF] outline-none text-sm bg-white"
              data-testid="ms-variant-select"
            >
              <option value="winner">Use promoted winner</option>
              <option value="all">All variants (default)</option>
              <option value="variant_a">Variant A only</option>
              <option value="variant_b">Variant B only</option>
              <option value="variant_c">Variant C only</option>
            </select>
            <p className="text-[10px] text-[#A3A3A3] mt-1">{filterDescriptions[variantFilter]}</p>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1">
            Notes (optional)
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. 'catching up after the throttle change'"
            maxLength={2000}
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0055FF] outline-none text-sm"
            data-testid="ms-notes-input"
          />
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!!activeJob || Number(count) < 1 || Number(delay) < preview.limits.min_delay_seconds}
            className="px-6 py-2.5 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#0040CC] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ms-send-btn"
          >
            Send {count} now
          </button>
          <span className="text-xs text-[#525252]">
            Estimated runtime: <strong>{estMin} min</strong>
          </span>
          {!!activeJob && (
            <span className="text-[10px] text-[#A3A3A3]">Wait for the current burst to finish.</span>
          )}
        </div>
      </div>

      {/* Variant breakdown */}
      {preview.variant_breakdown.length > 0 && (
        <div className="border border-[#E5E5E5] bg-white p-4">
          <h4 className="text-xs font-black uppercase tracking-[0.16em] text-[#0A0A0A] mb-3">
            Variant breakdown per step (due now)
          </h4>
          <div className="space-y-3">
            {preview.variant_breakdown.map((step) => (
              <div key={step.step_index} className="border-l-2 border-[#0055FF] pl-3">
                <p className="text-xs font-bold text-[#0A0A0A]">
                  Step {step.step_index + 1}:
                  <span className="font-normal text-[#525252] ml-1">{step.step_subject_hint || "(no subject)"}</span>
                </p>
                <div className="mt-1 flex flex-wrap gap-3 text-[11px]">
                  {step.variants.map((v) => (
                    <span
                      key={v.id}
                      className={`px-2 py-1 border tabular-nums ${
                        v.is_winner
                          ? "border-[#15803D] bg-[#F0FDF4] text-[#15803D] font-bold"
                          : "border-[#E5E5E5] text-[#525252]"
                      }`}
                    >
                      {v.label}: {v.due_now} {v.is_winner && "★ winner"}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent burst history */}
      <div className="border border-[#E5E5E5] bg-white p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-xs font-black uppercase tracking-[0.16em] text-[#0A0A0A]">Recent bursts</h4>
          <button
            onClick={loadHistory}
            className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF]"
          >
            Refresh
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-[#A3A3A3] py-2">No bursts yet.</p>
        ) : (
          <table className="w-full text-xs" data-testid="ms-history-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#A3A3A3] text-left">
                <th className="py-2">Job</th>
                <th>Status</th>
                <th className="tabular-nums">Sent / Total</th>
                <th>Filter</th>
                <th>Delay</th>
                <th>Started</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {history.map((j) => (
                <tr key={j.job_id} className="border-t border-[#F5F5F5]" data-testid={`ms-history-${j.job_id}`}>
                  <td className="py-2 font-mono text-[10px]" title={j.job_id}>
                    {j.job_id.slice(-10)}
                  </td>
                  <td><BurstStatusBadge status={j.status} /></td>
                  <td className="tabular-nums">
                    {j.progress_sent}/{j.progress_total}
                    {j.progress_failed > 0 && <span className="text-[#DC2626] ml-1">·{j.progress_failed}f</span>}
                    {/* Stopped-early hint — when sent < total AND status is done,
                        surface the reason inline so the operator doesn't have
                        to wonder why 18/280 means "complete". */}
                    {j.status === "done" && j.progress_sent < j.progress_total && (
                      <span className="ml-2 text-[10px] text-[#A3A3A3] italic"
                            title={j.last_error || "Stopped early"}>
                        early ↗
                      </span>
                    )}
                  </td>
                  <td className="text-[#525252]">{j.variant_filter}</td>
                  <td className="text-[#525252] tabular-nums">{j.delay_seconds}s</td>
                  <td className="text-[#A3A3A3]">{j.started_at ? new Date(j.started_at).toLocaleString() : "—"}</td>
                  <td className="text-[#525252]">{j.started_by_name || j.started_by?.slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => !submitting && setConfirmOpen(false)}
        >
          <div
            className="bg-white border-2 border-[#0055FF] max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="ms-confirm-modal"
          >
            <h3 className="text-lg font-black tracking-tight mb-2">Confirm burst</h3>
            <div className="text-sm space-y-2 text-[#525252] mb-4">
              <p>About to send <strong className="text-[#0A0A0A]">{count} emails</strong> from the <strong className="text-[#0A0A0A]">{preview.workflow_name}</strong> workflow with a <strong className="text-[#0A0A0A]">{delay}-second gap</strong> between sends.</p>
              <p>Filter: <strong className="text-[#0A0A0A]">{variantFilter}</strong></p>
              <p>Estimated runtime: <strong className="text-[#0A0A0A]">{estMin} min</strong></p>
              <p className="text-[11px] text-[#A3A3A3]">You can cancel mid-flight from the active-job panel above.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                className="px-4 py-2 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider text-[#525252] hover:bg-[#F5F5F5]"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-6 py-2 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#0040CC] disabled:opacity-40"
                data-testid="ms-confirm-send-btn"
              >
                {submitting ? "Starting…" : `Start burst`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{label}</p>
      <p
        className="text-xl font-black tabular-nums mt-0.5"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function BurstStatusBadge({ status }) {
  const config = {
    queued:    { cls: "border-[#A3A3A3] text-[#525252] bg-white", label: "Queued" },
    running:   { cls: "border-[#0055FF] text-[#0055FF] bg-[#F0F7FF]", label: "Running" },
    done:      { cls: "border-[#15803D] text-[#15803D] bg-[#F0FDF4]", label: "Done" },
    cancelled: { cls: "border-[#D97706] text-[#D97706] bg-[#FFFBEB]", label: "Cancelled" },
    errored:   { cls: "border-[#DC2626] text-[#DC2626] bg-[#FEF2F2]", label: "Errored" },
  }[status] || { cls: "border-[#A3A3A3] text-[#525252] bg-white", label: status };
  return (
    <span className={`inline-block px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-wider ${config.cls}`}>
      {config.label}
    </span>
  );
}
